/**
 * 카테고리 도메인 — URL 파싱 · 코드 매핑 · 입점몰 · 페이지 판정.
 */

// 1) URL 파싱
/**
 * URL 세그먼트 → 카테고리 경로. 예) ['women','001001008'] → 대 001 / 중 001 / 소 008
 * 없는 뎁스는 '' 로 정규화, 검증 실패 시 null.
 */
export const CATEGORY_TYPES = [
  'women',
  'men',
  'beauty',
  'life',
  'kids',
  'sports',
  'food',
] as const;

export type CategoryType = (typeof CATEGORY_TYPES)[number];

export interface CategoryPathInfo {
  categoryType: CategoryType;
  categorySubType: string;
  largeCategory: string;
  middleCategory: string;
  smallCategory: string;
}

export const isCategoryType = (raw: string): raw is CategoryType =>
  (CATEGORY_TYPES as readonly string[]).includes(raw);

export const parseCategoryPath = (
  segments: readonly string[]
): CategoryPathInfo | null => {
  const [rawType, rawSubType] = segments;

  // 세그먼트는 최대 2개(type, subType), subType 은 3/6/9자리 숫자만 허용
  if (!rawType || segments.length > 2) {
    return null;
  }
  if (rawSubType && !/^\d{3}(\d{3}){0,2}$/.test(rawSubType)) {
    return null;
  }
  const categoryType = rawType.toLowerCase();
  if (!isCategoryType(categoryType)) {
    return null;
  }

  const categorySubType = rawSubType || '000';
  const largeCategory = rawSubType ? categorySubType.substring(0, 3) : '000';
  const middleRaw = categorySubType.substring(3, 6);
  // 중분류 없음('000' 패딩)도 소분류와 동일하게 '' 로 통일
  const middleCategory = !middleRaw || middleRaw === '000' ? '' : middleRaw;

  const smallRaw = categorySubType.substring(6, 9);
  // 소분류 없음(6자리 또는 '000' 패딩)은 '' 로 통일
  const smallCategory = !smallRaw || smallRaw === '000' ? '' : smallRaw;

  return {
    categoryType,
    categorySubType,
    largeCategory,
    middleCategory,
    smallCategory,
  };
};

// 2) 코드 매핑
/** 카테고리 타입 ↔ 전시 mediumCd (신규 카테고리 추가 시 등록) */

export const CATEGORY_MEDIUM_CD: Record<CategoryType, string> = {
  women: 'M33439436',
  men: 'M83985861',
  beauty: 'M39593862',
  life: 'M64603019',
  kids: 'M67927064',
  sports: 'M51282714',
  food: 'M14735629',
};

export const getCategoryMediumCd = (categoryType: CategoryType): string =>
  CATEGORY_MEDIUM_CD[categoryType];

/** 카테고리 타입 → 한글명 */
export const CATEGORY_TYPE_NAME: Record<CategoryType, string> = {
  women: '여성',
  men: '남성',
  beauty: '뷰티',
  life: '라이프',
  kids: '키즈',
  sports: '스포츠',
  food: '푸드',
};

export const getCategoryTypeName = (categoryType: CategoryType): string =>
  CATEGORY_TYPE_NAME[categoryType];

/** SEO 전용 표기 (sports 만 '스포츠/액티브') */
export const SEO_CATEGORY_TYPE_NAME: Record<CategoryType, string> = {
  women: '여성',
  men: '남성',
  beauty: '뷰티',
  life: '라이프',
  kids: '키즈',
  sports: '스포츠/액티브',
  food: '푸드',
};

/** 라이프스타일 계열(성별 몰 외) */
export const LIFESTYLE_CATEGORY_TYPES: readonly CategoryType[] = [
  'beauty',
  'life',
  'kids',
  'sports',
  'food',
];

/** 네비게이션 API gender — women|men 만 받는다('All' 불가, 구전시 규칙) */
export const toNavigationGender = (genderType?: string): 'women' | 'men' =>
  genderType === 'Men' ? 'men' : 'women';

// 3) 입점몰
/** 입점몰 표시명 */
export const getShopDisplayName = (shopCds: readonly string[]): string => {
  if (!shopCds.length) {
    return '';
  }
  return shopCds[0] === '3005687' ? '시코르' : 'SSG 뷰티';
};

/** ?shopcd= → 샵코드 배열 (먼데이문은 코드 2개 묶음) */
const MONDAY_MOON_CODES = ['3007766', '3007765'];

export const resolveShopCds = (shopcd?: string): string[] => {
  if (!shopcd) {
    return [];
  }
  if (shopcd === 'MondayMoon' || shopcd === 'MONDAYMOON') {
    return MONDAY_MOON_CODES;
  }
  return [shopcd];
};

// 4) 페이지 판정
/** 페이지 형태 판정 — cateMain(대분류, 구좌) / cateList(LP 전용) */

export type CategoryPageType = 'cateMain' | 'cateList';

interface ResolvePageTypeInput {
  categoryType: CategoryType;
  categorySubType: string;
  shopCds: string[];
  isMobile: boolean;
}

export const resolvePageType = ({
  categoryType,
  categorySubType,
  shopCds,
  isMobile,
}: ResolvePageTypeInput): CategoryPageType => {
  if (categorySubType.length > 3) {
    return 'cateList';
  }
  if (categoryType === 'beauty' && shopCds.length > 0) {
    return 'cateList';
  }
  if (shopCds.length > 0 && categorySubType === '005') {
    return 'cateList';
  }
  if (categoryType === 'beauty' && categorySubType === '005' && !isMobile) {
    return 'cateList';
  }
  return 'cateMain';
};
